import { Injectable } from '@angular/core';
import { LOCALLY_HOSTED } from '../common/setup';
import { ActionStatus } from '../model';

const VERSION = 2; // increment this counter when a db table is changed/added

@Injectable()
export class IndexedDBService {

    private db: IDBDatabase;
    private dbName = 'servitlyOfflineDB';
    private generalStoreName = 'apiResponse';

    static DB_NOT_REFRENCED_ERROR = 'IndexedDB not referenced';

    constructor() {
        if (LOCALLY_HOSTED) {
            this.init();
        }
    }

    init(): Promise<void> {
        if (this.db) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, VERSION);
            request.onerror = () => reject(`Error on opening IndexedDB: ${request.error}`);
            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };
            request.onupgradeneeded = () => {
                this.db = request.result;
                if (!this.db.objectStoreNames.contains(this.generalStoreName)) {
                    this.db.createObjectStore(this.generalStoreName);
                }
                if (!this.db.objectStoreNames.contains('action')) {
                    const store = this.db.createObjectStore('action', { keyPath: 'id' });
                    store.createIndex('startTimestamp_idx', 'startTimestamp', { unique: false });
                    store.createIndex('page_thingId_idx', ['page', 'thingId'], { unique: false });
                    store.createIndex('page_thingId_workOrderId_idx', ['page', 'thingId', 'workOrderId'], { unique: false });
                }
                if (!this.db.objectStoreNames.contains('historicalAction')) {
                    const store = this.db.createObjectStore('historicalAction', { keyPath: 'id' });
                    store.createIndex('startTimestamp_idx', 'startTimestamp', { unique: false });
                }
            };
        });
    }

    save(url: string, data: any): Promise<void> {
        return this.doSave(this.generalStoreName, data, url);
    }

    private doSave(storeName: string, data: any, key?: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(IndexedDBService.DB_NOT_REFRENCED_ERROR);
            }
            const tx = this.db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            store.put(data, key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    saveResource(storeName: string, data: any): Promise<void> {
        return this.doSave(storeName, data);
    }

    get(url: string): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(IndexedDBService.DB_NOT_REFRENCED_ERROR);
            }
            const tx = this.db.transaction(this.generalStoreName, 'readonly');
            const store = tx.objectStore(this.generalStoreName);
            const request = store.get(url);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    getActionsPaginated(pageIndex: number, pageSize: number, sortDirection: 'asc' | 'desc', active: boolean,
        thingId: string, workOrderId?: string): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(IndexedDBService.DB_NOT_REFRENCED_ERROR);
            }
            const offset = pageIndex * pageSize;
            const storeName = active ? 'action' : 'historicalAction';
            const tx = this.db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const index = store.index('startTimestamp_idx');
            const actions: ActionStatus[] = [];

            // cursor iteration
            let direction: IDBCursorDirection = sortDirection === 'asc' ? 'next' : 'prev';
            const request = index.openCursor(null, direction);
            let skipped = 0;
            request.onsuccess = event => {
                const computePage = () => {
                    const isLast = actions.length < pageSize;
                    return {
                        content: actions,
                        number: pageIndex,
                        size: pageSize,
                        totalElements: offset + actions.length + (isLast ? 0 : 1),
                        last: isLast,
                        totalPages: pageIndex + (isLast ? 0 : 1),
                        first: !pageIndex,
                        numberOfElements: actions.length
                    }
                }
                const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                if (cursor) {
                    const action = cursor.value as ActionStatus;
                    if (action.thingId === thingId && (!workOrderId || workOrderId === action.workOrderId)) {
                        if (skipped < offset) {
                            skipped++;
                        } else if (actions.length < pageSize) {
                            actions.push(action);
                        } else {
                            // limit reached
                            resolve(computePage());
                            return;
                        }
                    }
                    cursor.continue();
                } else {
                    resolve(computePage());
                }
            };
            request.onerror = () => reject(request.error);
        });
    }

    deleteByIndex(storeName: string, indexName: string, key: any): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(IndexedDBService.DB_NOT_REFRENCED_ERROR);
            }
            const tx = this.db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const index = store.index(indexName);
            const range = IDBKeyRange.only(key);
            const request = index.openCursor(range);
            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                } else {
                    resolve();
                }
            };
            request.onerror = () => reject(request.error);
        });
    }

}